import React, { useEffect, useMemo, useState } from 'react';
import { Grid, Box, Typography, Skeleton } from '@mui/material';
import { useSelector } from 'react-redux';
import { CustomReactSelect,  
    HighchartBarChart, 
    CustomButtonGroup, 
    USACountryChart } from '../../../shared';
import { DISTRIBUTION_CHART_OPTION, USA_CHART_OPTIONS, hideDistrictMap } from './constants';
import { transformHospitalDataForBarChart, transformHospitalDataForMapChart } from '../../../../utility';

const BREAKDOWN_OPTIONS = ['Site', 'State'];
const SEGMENT_TYPES = [
    { label: 'All', value: 'all' },
    { label: 'Collaborators', value: 'collaborators' },
    { label: 'Non-Collaborators', value: 'non-collaborators' }
];

const segmentCompanies = [64,233,190,191,192];
const hideOverAll = [64];
function HospitalBreakdown() {

    const { company } = useSelector((state) => state.company);
    const { 
        hospitalEngagementData,
        hospitalBarChart,
        hospitalMapChart,
        hospitalStateBarChart,
        overallHospitalPhysicians,
        isLoading
    } = useSelector((state) => state.digitalEngagement);

    const [selectedBreakdown, setSelectedBreakdown] = useState(BREAKDOWN_OPTIONS[0]);
    const [engagementSegment, setEngagementSegment] = useState(SEGMENT_TYPES[0]); 
    const [barChartSeries, setBarChartSeries] = useState([]);
    const [filteredPhysicians, setFilteredPhysicians] = useState(0);
    const [filteredMapData, setFilteredMapData] = useState([]);

    const isFinalLoading = isLoading;
    const filterBarChartData = (category, breakdown) => {
        let filteredData = [], filteredPhysicianCount = 0;
        const data = breakdown === 'State' ? hospitalStateBarChart : hospitalBarChart;

        if (category === 'all') {
            return { barChartData: data, physicianCount: overallHospitalPhysicians };
        }
        const transformedData = transformHospitalDataForBarChart(
            hospitalEngagementData, 
            breakdown === 'State', 
            breakdown !== 'State', 
            category
        ) || [];
        filteredData = transformedData.filter(item => item[2].toLowerCase() === category);
        filteredPhysicianCount = filteredData.reduce((acc, curr) => acc + curr[1], 0);

        return { barChartData: filteredData, physicianCount: filteredPhysicianCount };
    };

    useEffect(() => {
        const breakdown = selectedBreakdown === 'State' || hideDistrictMap.includes(company.id) ? 'State' : 'Site';
        const { barChartData, physicianCount } = filterBarChartData(engagementSegment.value, breakdown);

        setBarChartSeries(barChartData);
        setFilteredPhysicians(physicianCount);
    }, [selectedBreakdown, engagementSegment, company.id, hospitalBarChart, hospitalStateBarChart, overallHospitalPhysicians]);

    useEffect(() => {
        setFilteredMapData([]);
        setTimeout(() => {
            if (engagementSegment.value === 'all') {
                setFilteredMapData([...hospitalMapChart]);
            } else {
                const filteredData = transformHospitalDataForMapChart(hospitalEngagementData, engagementSegment.value);
                setFilteredMapData(filteredData);
            }
        }, 0);
    }, [hospitalMapChart, engagementSegment, hospitalEngagementData]);
    

    const handleEngagementSegmentChange = (option) => {
        setEngagementSegment(option);
    };

    const handleBreakdownChange = (option) => {
        setSelectedBreakdown(option);
        setEngagementSegment(SEGMENT_TYPES[0]);
    };

    const mapChartOptions = useMemo(() => ({
        ...USA_CHART_OPTIONS,
        tooltip: {
            formatter: function () {
                const hospitalData = this.point.hospital
                    ? this.point.hospital
                        .map(hosp => Object.entries(hosp)
                            .map(([name, value]) => `<br>${name}: ${value}`))
                        .join('')
                    : 'No data available';

                return `<b>${this.point.name}</b><br/>Value: ${this.point.value}${hospitalData}`;
            }
        }
    }), [selectedBreakdown, hospitalMapChart]);
    return (
        <Box className="box-sizing-border-box padding-24px">
            {/* Header */}
            <Box className="padding-tb-20 border-bottom-gray flex-row-space-between-center">
                <Typography className="text-lg-semi-bold gray-900">
                    Active Site level Breakdown of Digital Physician Engagement
                </Typography>
                <CustomButtonGroup
                    data={BREAKDOWN_OPTIONS}
                    handleClick={handleBreakdownChange}
                    key="breakdown"
                    selected={selectedBreakdown}
                />
            </Box>

            {/* Content Section */}
            <Grid container className="flex-row-center">
                {/* Map Chart */}
                <Grid item className="us-bubble-map-container" lg={7} xl={8}>
                    <USACountryChart
                        chartOptions={mapChartOptions}
                        key={filteredMapData}
                        mapSeries={filteredMapData}
                    />
                </Grid>

                {/* Bar Chart & Segment Selection */}
                <Grid item lg={5} xl={4}>
                    <Box className="container-p-24-br-16 bg-gray-50 mt-24px">
                        <Box className="flex-column gap-4">
                            <Typography className="text-lg-semi-bold gray-900">
                                Active Site level Breakdown of Digital Physician Engagement
                            </Typography>
                            {
                                !hideOverAll.includes(company.id) &&
                            <Typography className="text-sm-regular gray-500">
                                {isLoading ? <Skeleton variant="text" width={200} /> : filteredPhysicians} Overall Physicians
                            </Typography>
                            }
                        </Box>

                        {segmentCompanies.includes(company.id) && (
                            <Box className="flex-row-space-between pt-12px">
                                {isLoading ? (
                                    <>
                                        <Skeleton height={60} variant="text" width={150} />
                                    </>
                                ) : (
                                    <Box className="w-100">
                                        <CustomReactSelect
                                            handleFilter={handleEngagementSegmentChange}
                                            label="Segment"
                                            options={SEGMENT_TYPES}
                                            placeholder="Select Segment"
                                            selectedOption={engagementSegment}
                                        />
                                    </Box>
                                )}
                            </Box>
                        )}

                        {/* Bar Chart */}
                        <Box className="pt-24px district-breakdown-bar-chart">
                            {isFinalLoading ? (
                                <Skeleton height={400} variant="rectangular" width="100%" />
                            ) : (
                                <HighchartBarChart 
                                    chartOptions={{
                                        ...DISTRIBUTION_CHART_OPTION,
                                        chart: {
                                            ...DISTRIBUTION_CHART_OPTION.chart,
                                            height: barChartSeries.length > 0 ? selectedBreakdown === 'State' ? barChartSeries.length * 35 : barChartSeries.length * 55 : 0
                                        }
                                    }}
                                    key={barChartSeries}
                                    seriesData={barChartSeries}
                                    seriesName="Digital Engagement"
                                />
                            )}
                        </Box>
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
}

export default HospitalBreakdown;
